import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-07 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/oriora_scout/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so data stays on-device
// (except `research`, which drives Scout's own agentic plan→gather→synthesize pipeline,
// unchanged). The regex intent-router below is the FREE FALLBACK (offline / signed-out /
// out of funds) — chat never goes dead. Original prototype notes:
//   • "research <q>" / "look up <q>" / "investigate <q>" / a bare "…?" question → research (the ONE billed path)
//   • "search <q>" / "show <q>" / "list <q>" / "find <q>"                       → filter   (local FTS, free)
//   • "summarize / overview / how many / stats"                                 → summarize (local, free)
//   • "delete <q>" / "remove <q>"                                               → delete_brief (HITL confirm)
//   • "clear history" / "delete all"                                            → clear_history (HITL confirm)
//   • anything else                                                             → none + a short nudge
//
// No domain-advice fence exists in this app (Scout's ONE AI action grounds a cited research
// brief from web sources — it doesn't advise/diagnose/recommend), so none is added here either —
// fence-by-catalog-omission, matching the original design. Scout stays BYOK-only (agentic); a
// missing vendor key surfaces as a BrainError from runResearch, shown in the chat, exactly as before.
// Guard against accidental billed runs: the `research` catalog entry's description tells the
// server model to pick it ONLY for an explicit research ask or a genuine question — never for
// ambiguous chit-chat — mirroring the old regex's requirement of an explicit verb or a "?".

import { store, type BriefMeta } from "../store";
import { BrainError, brainCall } from "../brain";
import type { ChatAction, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry search matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface (Scout is NOT reply-discarded — see fence note above)
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

const HELP =
  "I can research a question, search your saved briefs, summarize your history, and delete briefs — just tell me. Try:\n" +
  '• "research the evidence on creatine for cognition"\n' +
  '• "How do heat pumps work?" (end with a ? to research)\n' +
  '• "search caffeine" · "summarize" · "delete <brief>" · "clear history"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// Forgiving brief lookup by question: an exact question still wins, otherwise the FLEET fuzzy
// resolver (see ./fuzzy) tolerates misspellings, dropped words and word order, with a summary
// substring fallback. Briefs key on `question`, so we adapt them to the resolver's `name`.
async function findByQuestion(term: string): Promise<BriefMeta[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listBriefs(50000);
  const exact = all.filter((b) => b.question.toLowerCase() === q);
  if (exact.length) return exact;
  const byQ = findNamedMatches(all.map((b) => ({ ...b, name: b.question })), term);
  if (byQ.length) return byQ;
  return all.filter((b) => b.summary?.toLowerCase().includes(q) ?? false);
}

/** "Couldn't find a saved brief matching X" enriched with the closest questions ("did you mean …?"). */
async function briefsNotFound(term: string): Promise<string> {
  const all = await store.listBriefs(50000);
  return notFoundMessage(all.map((b) => ({ name: b.question })), term);
}

function pickList(rows: BriefMeta[]): string {
  return rows.slice(0, 5).map((b) => `• ${b.question}`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "byok_required")) {
      const r0 = none(e.message);
      return { reply: r0.reply, actions: [r0.action] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (question text → id) locally, because
// the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.scout.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "research", description: "Run Oriora Scout's deep-research pipeline (plan → gather → synthesize — several billed steps) on a question, saving a cited brief to history. Use ONLY for an explicit research/look-up/investigate ask, or a genuine question — never for vague chit-chat.", params: { question: "REQUIRED — the research question, as the user asked it" } },
  { name: "filter", description: "Search the user's saved research briefs (local, free). Use for search/show/list asks.", params: { query: "optional free text to search saved questions/summaries" } },
  { name: "summarize", description: "Show local history stats: completed briefs, sources cited, in-progress/unfinished counts. No params.", params: {} },
  { name: "delete_brief", description: "Delete one saved brief identified by its question text (the app asks the user to confirm first).", params: { question: "which brief — question text as the user wrote it" } },
  { name: "update_brief", description: "Give a saved brief the customer's OWN title, or write their notes on it — what they made of it, what to follow up. Send ONLY what is changing. Use for 'call that brief X', 'title it...', 'note on that one that...'. It does NOT change the researched question, the answer or the sources — those stay as they were researched.", params: { question: "REQUIRED — which brief, by its question or current title", title: "optional — the customer's own title for it", notes: "optional — the customer's own notes on it" } },
  { name: "clear_history", description: "Delete ALL saved research history (the app asks the user to confirm first). No params.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE research history instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const briefs = await store.listBriefs(40);
  const list = briefs.map((b) => b.question).join(", ");
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Saved research briefs: ${list || "(none yet)"}.`)
    + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  // brainCall's optional params here are (signal, onChunk) — no abort signal for chat.
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, undefined, onChunk);

  // The shared brain returns an ARRAY (G17): reading only the singular `action` is why a
  // compound ask silently did half the work. Scout keeps the model's reply (not fenced).
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

export async function toAction(r: ServerChat): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "research": {
      const question = asStr(p.question);
      if (!question) return none(say || 'What should I research? Try "research the evidence on creatine for cognition".');
      return { reply: say, action: { kind: "research", question } };
    }
    case "filter": {
      const query = asStr(p.query) ?? "";
      return { reply: "", action: { kind: "filter", query, label: query || undefined } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "delete_brief": {
      const term = asStr(p.question);
      if (!term) return none(say || "Which brief do you want to delete? Give me its question.");
      const rows = await findByQuestion(term);
      if (rows.length === 0) return none(await briefsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} briefs matching "${term}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "delete_brief", id: rows[0].id, label: rows[0].question } };
    }
    case "update_brief": {
      const term = asStr(p.question);
      if (!term) return none(say || "Which brief do you want to change? Give me its question or title.");
      const rows = await findByQuestion(term);
      if (rows.length === 0) return none(await briefsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} briefs matching "${term}" — be more specific:\n${pickList(rows)}`);
      const title = asStr(p.title), notes = asStr(p.notes);
      if (title === undefined && notes === undefined) return none(say || "What should I change — the title, or your notes?");
      const act: ChatAction = { kind: "update_brief", id: rows[0].id, label: rows[0].title || rows[0].question };
      if (title !== undefined) act.title = title;
      if (notes !== undefined) act.notes = notes;
      return { reply: say, action: act };
    }
    case "clear_history":
      return { reply: say, action: { kind: "clear_history" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 1) SUMMARIZE — local history stats (free).
  if (/\b(summar(?:y|ise|ize)|overview|how\s+many|stats?)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 2) CLEAR ALL — wipe history (HITL confirm).
  if (/^(?:clear|wipe|delete|remove)\s+(?:all|everything|(?:my\s+)?history|all\s+(?:my\s+)?(?:research|briefs?))\s*$/i.test(t)) {
    return { reply: "", action: { kind: "clear_history" } };
  }

  // 3) DELETE <q> — resolve a brief; the UI asks for confirmation before executing.
  let m = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:brief\s+)?(.+)$/i);
  if (m) {
    const rows = await findByQuestion(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await briefsNotFound(m[1]) } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} briefs matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "delete_brief", id: rows[0].id, label: rows[0].question } };
  }

  // 4) RESEARCH verbs — the ONE billed path (plan/gather/synthesize). "find out" lives here, not search.
  m = t.match(/^(?:research|look\s+up|investigate|dig\s+into|deep\s+dive(?:\s+on|\s+into)?|find\s+out|explore)\s+(?:about\s+|on\s+|into\s+)?(.+)$/i);
  if (m) return { reply: "", action: { kind: "research", question: m[1].trim() } };

  // 5) SEARCH / SHOW — local FTS over saved briefs (free). Bare "find <q>" means search here.
  m = t.match(/^(?:search|find|show|list)\s+(?:me\s+)?(?:all\s+)?(?:my\s+)?(?:saved\s+)?(?:research|briefs?)?\s*(?:on\s+|about\s+|for\s+)?(.*)$/i);
  if (m) {
    const q = m[1].trim();
    return { reply: "", action: { kind: "filter", query: q, label: q || undefined } };
  }

  // 6) A bare question (ends with "?") — treat the whole thing as a research question (Scout's core).
  if (/\?\s*$/.test(t)) return { reply: "", action: { kind: "research", question: t } };

  // 7) Unclear → a short nudge (never auto-fire a billed run on ambiguous input).
  return { reply: "", action: { kind: "none", message: HELP } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
